import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Menu, Paper, TextField, Tooltip, Typography, useTheme } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import EmojiEmotionsOutlinedIcon from '@mui/icons-material/EmojiEmotionsOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import InboxAudioRecorder from '../../../components/Inbox/InboxAudioRecorder';
import QuickMessagesButton from '../../../components/QuickMessagesButton';
import type { TeamChatMessage } from '../../../services/teamChatService';
import { TEAM_CHAT_DEFAULT_QUICK_MESSAGES } from '../teamChatQuickMessages';
import {
  getTeamChatDockZ,
  TEAM_CHAT_THEME,
  teamChatComposerFieldSx,
  teamChatComposerPaperSx,
  teamChatIconButtonSx,
  teamChatSendButtonSx,
} from '../teamChatTheme';
import { formatFileSize } from '../teamChatUtils';

const EMOJI_PALETTE = [
  '😀', '😁', '😂', '🙂', '😉', '😊', '😍', '🤔',
  '👍', '👏', '🙏', '💪', '🔥', '✅', '❗', '⚠️',
  '❤️', '🎉', '📅', '📌', '📎', '💬', '🩺', '💉',
];

interface TeamChatComposerProps {
  disabled?: boolean;
  sending: boolean;
  replyTo: TeamChatMessage | null;
  editing: TeamChatMessage | null;
  onCancelReply: () => void;
  onCancelEdit: () => void;
  onSend: (body: string) => void;
  onSendFile: (file: File) => void;
  onSubmitEdit: (body: string) => void;
  onTyping: (isTyping: boolean) => void;
  onError: (message: string) => void;
}

const TeamChatComposer: React.FC<TeamChatComposerProps> = ({
  disabled,
  sending,
  replyTo,
  editing,
  onCancelReply,
  onCancelEdit,
  onSend,
  onSendFile,
  onSubmitEdit,
  onTyping,
  onError,
}) => {
  const theme = useTheme();
  const dockZ = getTeamChatDockZ(theme);
  const [text, setText] = useState('');
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [emojiAnchor, setEmojiAnchor] = useState<HTMLElement | null>(null);
  const [recording, setRecording] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const typingTimeoutRef = useRef<number | null>(null);
  const typingActiveRef = useRef(false);

  useEffect(() => {
    if (editing) {
      setText(editing.body || '');
      inputRef.current?.focus();
    }
  }, [editing]);

  useEffect(() => {
    if (replyTo) inputRef.current?.focus();
  }, [replyTo]);

  // Sinaliza "digitando" no início e agenda a parada; evita um emit por tecla.
  const signalTyping = () => {
    if (!typingActiveRef.current) {
      typingActiveRef.current = true;
      onTyping(true);
    }
    if (typingTimeoutRef.current) window.clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = window.setTimeout(() => {
      typingActiveRef.current = false;
      onTyping(false);
    }, 2200);
  };

  const stopTyping = () => {
    if (typingTimeoutRef.current) window.clearTimeout(typingTimeoutRef.current);
    if (typingActiveRef.current) {
      typingActiveRef.current = false;
      onTyping(false);
    }
  };

  useEffect(
    () => () => {
      if (typingTimeoutRef.current) window.clearTimeout(typingTimeoutRef.current);
      if (typingActiveRef.current) {
        typingActiveRef.current = false;
        onTyping(false);
      }
    },
    [onTyping],
  );

  const submit = () => {
    if (sending) return;

    if (pendingFile) {
      onSendFile(pendingFile);
      setPendingFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const value = text.trim();
    if (!value) return;

    stopTyping();
    if (editing) onSubmitEdit(value);
    else onSend(value);
    setText('');
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit();
      return;
    }
    if (event.key === 'Escape') {
      if (editing) onCancelEdit();
      if (replyTo) onCancelReply();
    }
  };

  const handleFilePicked = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      onError('O arquivo deve ter no máximo 25 MB.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setPendingFile(file);
  };

  const banner = editing
    ? { label: 'Editando mensagem', text: editing.body || '', onClose: onCancelEdit }
    : replyTo
      ? {
          label: `Respondendo ${replyTo.sender?.full_name || 'mensagem'}`,
          text: replyTo.body || replyTo.attachment_name || 'Anexo',
          onClose: onCancelReply,
        }
      : null;

  return (
    <Paper sx={teamChatComposerPaperSx}>
      <Box sx={{ px: 0, py: 0 }}>
      {banner && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1,
            px: 1.25,
            py: 0.85,
            borderRadius: 1.5,
            borderLeft: `3px solid ${TEAM_CHAT_THEME.primary}`,
            bgcolor: TEAM_CHAT_THEME.primarySoft,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: TEAM_CHAT_THEME.primary }}>
              {banner.label}
            </Typography>
            <Typography noWrap sx={{ fontSize: '0.76rem', color: TEAM_CHAT_THEME.slateMid }}>
              {banner.text}
            </Typography>
          </Box>
          <IconButton size="small" onClick={banner.onClose}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      )}

      {pendingFile && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1,
            px: 1.25,
            py: 0.85,
            borderRadius: 1.5,
            border: `1px solid ${TEAM_CHAT_THEME.border}`,
            bgcolor: TEAM_CHAT_THEME.tealSoft,
          }}
        >
          <InsertDriveFileOutlinedIcon sx={{ fontSize: 20, color: TEAM_CHAT_THEME.tealDark }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography noWrap sx={{ fontSize: '0.8rem', fontWeight: 600, color: TEAM_CHAT_THEME.navy }}>
              {pendingFile.name}
            </Typography>
            <Typography sx={{ fontSize: '0.68rem', color: TEAM_CHAT_THEME.slate }}>
              {formatFileSize(pendingFile.size)}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => {
              setPendingFile(null);
              if (fileInputRef.current) fileInputRef.current.value = '';
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5 }}>
        {!recording && (
          <>
            <Tooltip title="Anexar arquivo">
              <IconButton
                size="small"
                disabled={disabled || sending || Boolean(editing)}
                onClick={() => fileInputRef.current?.click()}
                sx={teamChatIconButtonSx}
              >
                <AttachFileIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Emojis">
              <IconButton
                size="small"
                disabled={disabled}
                onClick={(e) => setEmojiAnchor(e.currentTarget)}
                sx={teamChatIconButtonSx}
              >
                <EmojiEmotionsOutlinedIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
            <QuickMessagesButton
              channel="team_chat"
              size="small"
              overlayZIndex={dockZ.overlay}
              disabled={disabled || sending || Boolean(editing) || Boolean(pendingFile)}
              defaultMessages={TEAM_CHAT_DEFAULT_QUICK_MESSAGES}
              tooltip="Ações rápidas do consultório"
              onSelectMessage={(message) => {
                setText(message);
                stopTyping();
                setTimeout(() => inputRef.current?.focus(), 0);
              }}
            />
          </>
        )}

        {!recording && (
          <TextField
            inputRef={inputRef}
            fullWidth
            multiline
            maxRows={5}
            size="small"
            placeholder={pendingFile ? 'Enviar arquivo…' : 'Escreva uma mensagem'}
            value={text}
            disabled={disabled || Boolean(pendingFile)}
            onChange={(e) => {
              setText(e.target.value);
              signalTyping();
            }}
            onBlur={stopTyping}
            onKeyDown={handleKeyDown}
            sx={teamChatComposerFieldSx}
          />
        )}

        {!editing && !pendingFile && !text.trim() && (
          <InboxAudioRecorder
            disabled={disabled || sending}
            onRecordingChange={setRecording}
            onComplete={(file) => onSendFile(file)}
            onError={onError}
            size="small"
          />
        )}

        {!recording && (text.trim() || pendingFile || editing) && (
          <IconButton onClick={submit} disabled={disabled || sending} sx={teamChatSendButtonSx}>
            <SendIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        hidden
        onChange={handleFilePicked}
        accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip"
      />

      <Menu
        anchorEl={emojiAnchor}
        open={Boolean(emojiAnchor)}
        onClose={() => setEmojiAnchor(null)}
        sx={{ zIndex: dockZ.overlay }}
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', px: 1, maxWidth: 300 }}>
          {EMOJI_PALETTE.map((emoji) => (
            <IconButton
              key={emoji}
              size="small"
              onClick={() => {
                setText((prev) => `${prev}${emoji}`);
                setEmojiAnchor(null);
                inputRef.current?.focus();
              }}
              sx={{ fontSize: '1.05rem' }}
            >
              {emoji}
            </IconButton>
          ))}
        </Box>
      </Menu>
      </Box>
    </Paper>
  );
};

export default TeamChatComposer;
